import { apiClient, requiresAuth } from '../client';
import { School } from '../../types';

export const schoolsService = {
  getAllSchools: async () => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.get('/school');
        console.log(
          '[API] schoolsService.getAllSchools - Response data type:',
          typeof response.data
        );
        console.log(
          '[API] schoolsService.getAllSchools - Is response.data an array?',
          Array.isArray(response.data)
        );
        if (!Array.isArray(response.data)) {
          console.log(
            '[API] schoolsService.getAllSchools - Response data structure:',
            response.data
          );
        }

        console.log(
          '[API] schoolsService.getAllSchools - Fetching all schools - END',
          response
        );
        const data = response.data.schools || response.data;
        return Array.isArray(data) ? data : [];
      });
    } catch (error) {
      console.error(
        '[API] schoolsService.getAllSchools - Error fetching schools:',
        error
      );
      throw error;
    }
  },

  getSchoolById: async (id: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.get(`/school/${id}`);
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.getSchoolById - Error fetching school ${id}:`,
        error
      );
      throw error;
    }
  },

  createSchool: async (schoolData: Partial<School>) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.post('/school', schoolData);
        return response.data;
      });
    } catch (error) {
      console.error(
        '[API] schoolsService.createSchool - Error creating school:',
        error
      );
      throw error;
    }
  },

  updateSchool: async (id: string, schoolData: Partial<School>) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.put(`/school/${id}`, schoolData);
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.updateSchool - Error updating school ${id}:`,
        error
      );
      throw error;
    }
  },

  deleteSchool: async (id: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.delete(`/school/${id}`);
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.deleteSchool - Error deleting school ${id}:`,
        error
      );
      throw error;
    }
  },

  getSchoolAdmins: async (schoolId: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.get(`/school/${schoolId}/admins`);
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.getSchoolAdmins - Error fetching admins for school ${schoolId}:`,
        error
      );
      throw error;
    }
  },

  removeSchoolAdmin: async (schoolId: string, userId: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.delete(
          `/school/${schoolId}/admins/${userId}`
        );
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.removeSchoolAdmin - Error removing admin from school ${schoolId}:`,
        error
      );
      throw error;
    }
  },

  // Campus management endpoints
  getSchoolCampuses: async (schoolId: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.get(`/school/${schoolId}/campuses`);
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.getSchoolCampuses - Error fetching campuses for school ${schoolId}:`,
        error
      );
      throw error;
    }
  },

  attachCampusToSchool: async (schoolId: string, campusId: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.post(
          `/school/${schoolId}/campus/${campusId}`,
          {}
        );
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.attachCampusToSchool - Error attaching campus ${campusId} to school ${schoolId}:`,
        error
      );
      throw error;
    }
  },

  detachCampusFromSchool: async (schoolId: string, campusId: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.delete(
          `/school/${schoolId}/campus/${campusId}`
        );
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.detachCampusFromSchool - Error detaching campus ${campusId} from school ${schoolId}:`,
        error
      );
      throw error;
    }
  },

  // User management for schools
  getSchoolUsers: async (schoolId: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.get(`/school/${schoolId}/users`);
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.getSchoolUsers - Error fetching users for school ${schoolId}:`,
        error
      );
      throw error;
    }
  },

  assignUserToSchool: async (schoolId: string, userId: string, campusId?: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.post(
          `/school/${schoolId}/user/${userId}`,
          { campus_id: campusId }
        );
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.assignUserToSchool - Error assigning user ${userId} to school ${schoolId}:`,
        error
      );
      throw error;
    }
  },

  removeUserFromSchool: async (schoolId: string, userId: string) => {
    try {
      return requiresAuth(async () => {
        const response = await apiClient.delete(
          `/school/${schoolId}/user/${userId}`
        );
        return response.data;
      });
    } catch (error) {
      console.error(
        `[API] schoolsService.removeUserFromSchool - Error removing user ${userId} from school ${schoolId}:`,
        error
      );
      throw error;
    }
  },
};
